import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import { MobileSangria, SANGRIA_REASONS } from '../../component/cashregister/MobileCashRegisters';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import useIsMobileShell from '../../hooks/useIsMobileShell';
import { useSangria } from './components/useCashActions';
import { formatBRMoney, parseBRMoney } from './shared';

// `/cashregisters/sangria/:id` deixou de ser uma página de página cheia — a
// ação "Sangria" agora é um modal central real (`SangriaModal`, §5.6),
// acionado a partir do menu dropdown da Lista de caixas e do header do
// Painel do caixa (ver FRONTEND-TASKS.md, item "Sangria"). Acessar esta rota
// diretamente só redireciona para o Painel do caixa, mesmo precedente de
// `pages/categories/Form.js`.
//
// Shell mobile: a rota volta a ser uma página cheia (`caixas/sangria.html`),
// com a mesma regra/endpoint do modal (`useSangria`). O motivo enviado ao
// backend (`reason`, texto único) é "Motivo" + ": Observação" (quando houver).
const REASON_MAX = 120;

function MobileSangriaPage({ id }) {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [balance, setBalance] = useState(0);
    const [reasonOption, setReasonOption] = useState(SANGRIA_REASONS[0]);
    const [obs, setObs] = useState('');
    const [snack, setSnack] = useState({ open: false, message: '' });
    useAppBarTitle('Sangria');

    useEffect(() => {
        let active = true;
        Promise.all([
            httpClient.get(`/cashregister/${id}`),
            httpClient.get(`/cashregister/${id}/resume`),
        ]).then(([dataRes, resumeRes]) => {
            if (!active) return;
            if (dataRes.status === 200) setName(dataRes.data?.name || '');
            if (resumeRes.status === 200) setBalance(parseBRMoney(resumeRes.data?.available_cash));
        }).catch(() => {});
        return () => { active = false; };
    }, [id]);

    const { amount, amountError, reasonError, saving, changeAmount, changeReason, confirm } = useSangria({
        open: true,
        cashRegister: { id, name },
        onSuccess: ({ amount: value }) => navigate(`/cashregisters/view/${id}`, {
            replace: true,
            state: { toast: `Sangria de R$ ${formatBRMoney(parseBRMoney(value))} registrada em "${name}".` },
        }),
        onError: (data) => setSnack({
            open: true,
            message: typeof data === 'string' ? data : (data?.message || data?.error || 'Não foi possível concluir a operação.'),
        }),
    });

    const obsMaxLength = Math.max(REASON_MAX - reasonOption.length - 2, 0);
    useEffect(() => {
        const note = obs.trim();
        changeReason(note ? `${reasonOption}: ${note}`.slice(0, REASON_MAX) : reasonOption);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reasonOption, obs]);

    return (
        <PageScaffold
            links={[]}
            openSnackbar={snack.open}
            setOpenSnackbar={(open) => setSnack((prev) => ({ ...prev, open }))}
            message={snack.message}
            severity="error"
        >
            <MobileSangria
                balance={balance}
                amount={amount}
                onAmountChange={changeAmount}
                amountError={amountError}
                reasonOption={reasonOption}
                onReasonOptionChange={setReasonOption}
                reasonError={reasonError}
                obs={obs}
                onObsChange={setObs}
                obsMaxLength={obsMaxLength}
                saving={saving}
                onCancel={() => navigate(-1)}
                onConfirm={confirm}
            />
        </PageScaffold>
    );
}

export default function CashRegisterSangria() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isMobileShell = useIsMobileShell();

    useEffect(() => {
        if (!isMobileShell) navigate(`/cashregisters/view/${id}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isMobileShell]);

    return isMobileShell ? <MobileSangriaPage id={id} /> : null;
}
